import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, ArrowRight, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { PublicNavbar } from "@/components/layout/PublicNavbar";
import { Footer } from "@/components/layout/Footer";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api, getErrMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TIERS = [
  {
    name: "Standard Access",
    tier: "standard",
    price: 79,
    features: ["Weekly intelligence drops", "Deal summaries", "Deep-dive memos (standard)"],
  },
  {
    name: "Professional Access",
    tier: "professional",
    price: 199,
    features: ["Full deal breakdowns", "Execution pathways", "Priority insights", "Pro-tier memos"],
    highlight: true,
  },
  {
    name: "Institutional / Deal Room",
    tier: "institutional",
    price: null,
    features: ["Live deal pipeline", "Direct introductions", "Monthly private call", "Allocation pathways"],
  },
];

function Inner({ children }) {
  const { user } = useAuth();
  return user ? <DashboardLayout>{children}</DashboardLayout> : (
    <>
      <PublicNavbar />
      {children}
      <Footer />
    </>
  );
}

export default function Pricing() {
  const [processing, setProcessing] = useState(null);
  const [card, setCard] = useState({ number: "4242 4242 4242 4242", exp: "12/28", cvc: "123", name: "" });
  const [showCheckout, setShowCheckout] = useState(null);
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const selectTier = (tier) => {
    if (!user) {
      navigate("/auth", { state: { tier } });
      return;
    }
    if (tier === "institutional") {
      toast.info("Institutional tier is by application", { description: "A team member will reach out within 48h." });
      return;
    }
    setShowCheckout(tier);
  };

  const confirmCheckout = async () => {
    const tier = showCheckout;
    setProcessing(tier);
    try {
      await new Promise((r) => setTimeout(r, 800)); // fake processing
      const { data } = await api.post("/subscriptions/checkout", { tier });
      await refresh();
      toast.success(`${tier === "professional" ? "Professional" : "Standard"} access unlocked`, {
        description: `Simulated charge of $${data.subscription.amount_usd}. Payment integration will replace this.`,
      });
      setShowCheckout(null);
      navigate("/dashboard");
    } catch (err) {
      toast.error("Checkout failed", { description: getErrMsg(err) });
    } finally {
      setProcessing(null);
    }
  };

  return (
    <Inner>
      <div className="max-w-[1200px] mx-auto px-6 md:px-8 py-16 md:py-24">
        <div className="text-center max-w-xl mx-auto">
          <span className="overline gold-text">Access Tiers</span>
          <h1 className="font-display font-medium text-3xl md:text-5xl mt-4 tracking-tight">
            Choose Your Level of Access
          </h1>
          <p className="text-sm text-muted-foreground mt-4">
            Payment flow is currently <span className="gold-text">simulated</span>. Live Stripe processing will be added post-API key delivery.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-14">
          {TIERS.map((t) => (
            <div
              key={t.tier}
              className={`card-institutional p-8 relative flex flex-col ${t.highlight ? "border-[hsl(var(--gold))]" : ""} ${
                user?.tier === t.tier ? "ring-1 ring-[hsl(var(--gold))]" : ""
              }`}
              data-testid={`pricing-tier-${t.tier}`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 gold-bg text-[10px] font-semibold uppercase tracking-[0.2em] px-3 py-1 rounded-full text-[hsl(var(--accent-foreground))]">
                  Most Popular
                </div>
              )}
              <h3 className="font-display font-medium text-xl">{t.name}</h3>
              <div className="divider my-5" />
              <ul className="space-y-2.5 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 gold-text mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6">
                {t.price ? (
                  <div className="flex items-baseline gap-1">
                    <span className="font-display font-medium text-3xl md:text-4xl">${t.price}</span>
                    <span className="text-sm text-muted-foreground">/month</span>
                  </div>
                ) : (
                  <div className="font-display font-medium text-2xl">By Application</div>
                )}
              </div>
              <button
                onClick={() => selectTier(t.tier)}
                disabled={user?.tier === t.tier}
                className={`mt-5 w-full ${t.highlight ? "btn-primary" : "btn-ghost"} disabled:opacity-50`}
                data-testid={`pricing-select-${t.tier}`}
              >
                {user?.tier === t.tier ? "Current Tier" : t.price ? "Subscribe" : "Apply"} {user?.tier !== t.tier && <ArrowRight className="h-4 w-4" />}
              </button>
            </div>
          ))}
        </div>

        {/* Checkout overlay (simulated) */}
        {showCheckout && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="checkout-modal">
            <div className="card-institutional w-full max-w-md p-8">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="h-5 w-5 gold-text" strokeWidth={1.5} />
                <span className="overline gold-text">Secure Checkout · Simulated</span>
              </div>
              <h2 className="font-display font-medium text-2xl tracking-tight">
                {showCheckout === "professional" ? "Professional" : "Standard"} Access
              </h2>
              <div className="font-mono text-3xl gold-text mt-2">
                ${showCheckout === "professional" ? 199 : 79}
                <span className="text-sm text-muted-foreground ml-2">/month</span>
              </div>

              <div className="divider my-6" />

              <div className="space-y-3">
                <div>
                  <label className="overline block mb-2">Cardholder name</label>
                  <input
                    value={card.name}
                    onChange={(e) => setCard({ ...card, name: e.target.value })}
                    placeholder={user?.name || "Full name"}
                    className="w-full h-11 bg-transparent border border-border rounded-md px-3 text-sm outline-none focus:border-[hsl(var(--gold))]"
                    data-testid="checkout-name"
                  />
                </div>
                <div>
                  <label className="overline block mb-2">Card number</label>
                  <input
                    value={card.number}
                    onChange={(e) => setCard({ ...card, number: e.target.value })}
                    className="w-full h-11 bg-transparent border border-border rounded-md px-3 text-sm font-mono outline-none focus:border-[hsl(var(--gold))]"
                    data-testid="checkout-card"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="overline block mb-2">Expiry</label>
                    <input value={card.exp} onChange={(e) => setCard({ ...card, exp: e.target.value })} className="w-full h-11 bg-transparent border border-border rounded-md px-3 text-sm font-mono outline-none focus:border-[hsl(var(--gold))]" data-testid="checkout-exp" />
                  </div>
                  <div>
                    <label className="overline block mb-2">CVC</label>
                    <input value={card.cvc} onChange={(e) => setCard({ ...card, cvc: e.target.value })} className="w-full h-11 bg-transparent border border-border rounded-md px-3 text-sm font-mono outline-none focus:border-[hsl(var(--gold))]" data-testid="checkout-cvc" />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={() => setShowCheckout(null)} className="btn-ghost flex-1" data-testid="checkout-cancel">Cancel</button>
                <button onClick={confirmCheckout} disabled={processing} className="btn-primary flex-1" data-testid="checkout-confirm">
                  {processing ? "Processing…" : "Pay (simulated)"}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground text-center mt-4">
                No real charge is made. Card details are not transmitted.
              </p>
            </div>
          </div>
        )}
      </div>
    </Inner>
  );
}
